import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createClient, getClients, removeClient, updateClient } from '../../core/api/endpoints/clients/clientsApi';
import { AddClient } from '../../core/domainModels/clients/AddClient';
import { Client } from '../../core/domainModels/clients/Client';
import { UpdateClient } from '../../core/domainModels/clients/UpdateClient';
import { TachoColumnState } from '../../core/domainModels/displayPreferences/tachoColumnState';
import { PersistConfig } from 'redux-persist';
import { fieldName } from '../../core/helpers/aggrid';
import autoMergeLevel1 from 'redux-persist/lib/stateReconciler/autoMergeLevel1';
import storage from 'redux-persist/lib/storage';

export interface ClientsState {
    clients: Array<Client>;
    fetchClientsPending: boolean;
    fetchClientsError: string;
    deleteClientPending: boolean;
    deleteClientError: string;
    addClientPending: boolean;
    addClientError: string;
    updateClientPending: boolean;
    updateClientError: string;
    displayPreferences: Array<TachoColumnState>;
    tableFilters: any;
}

const initialState: ClientsState = {
    clients: [],
    fetchClientsPending: false,
    fetchClientsError: '',
    deleteClientPending: false,
    deleteClientError: '',
    addClientPending: false,
    addClientError: '',
    updateClientPending: false,
    updateClientError: '',
    displayPreferences: [],
    tableFilters: {},
};

const name = 'clients';

const fetchClientsThunk = createAsyncThunk(`${name}/fetch`, async (params, thunkAPI) => {
    try {
        return await getClients();
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const deleteClientThunk = createAsyncThunk(`${name}/delete`, async (clientId: string, thunkAPI) => {
    try {
        return await removeClient(clientId);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const addClientThunk = createAsyncThunk(`${name}/add`, async (client: AddClient, thunkAPI) => {
    try {
        return await createClient(client);
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error.message);
    }
});

const updateClientThunk = createAsyncThunk(`${name}/update`, async (user: UpdateClient, thunkAPI) => {
    try {
        return await updateClient(user);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const clientsSlice = createSlice({
    name,
    initialState,
    reducers: {
        modifyClientsDisplayPreferences(state, action: PayloadAction<any>) {
            state.displayPreferences = action.payload;
        },
        modifyClientsTableFilters(state, action: PayloadAction<any>) {
            state.tableFilters = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchClientsThunk.pending, (state, action) => {
                state.fetchClientsPending = true;
                state.fetchClientsError = '';
            })
            .addCase(fetchClientsThunk.fulfilled, (state, action) => {
                state.fetchClientsError = '';
                state.clients = action.payload;
                state.fetchClientsPending = false;
            })
            .addCase(fetchClientsThunk.rejected, (state, action) => {
                state.fetchClientsPending = false;
                state.fetchClientsError = action.payload as string;
            })
            .addCase(deleteClientThunk.pending, (state, action) => {
                state.deleteClientPending = true;
                state.deleteClientError = '';
            })
            .addCase(deleteClientThunk.fulfilled, (state, action) => {
                state.deleteClientError = '';
                state.clients = state.clients.filter(item => item.id !== action.payload);
                state.deleteClientPending = false;
            })
            .addCase(deleteClientThunk.rejected, (state, action) => {
                state.deleteClientPending = false;
                state.deleteClientError = action.payload as string;
            })
            .addCase(addClientThunk.pending, (state, action) => {
                state.addClientPending = true;
                state.addClientError = '';
            })
            .addCase(addClientThunk.fulfilled, (state, action) => {
                state.deleteClientError = '';
                state.addClientPending = false;
            })
            .addCase(addClientThunk.rejected, (state, action) => {
                state.addClientPending = false;
                console.log('xxxx', action.payload);
                state.addClientError = action.payload as string;
            })
            .addCase(updateClientThunk.pending, (state, action) => {
                state.updateClientPending = true;
                state.updateClientError = '';
            })
            .addCase(updateClientThunk.fulfilled, (state, action) => {
                const updatedUserIndex = state.clients.findIndex(c => c.id === action.payload.clientId);
                if (updatedUserIndex !== -1) {
                    state.clients[updatedUserIndex] = { ...state.clients[updatedUserIndex], ...action.payload };
                }
                state.updateClientPending = false;
            })
            .addCase(updateClientThunk.rejected, (state, action) => {
                state.updateClientPending = false;
                state.updateClientError = action.payload as string;
            });
    },
});
export const clientsReducerConfig: PersistConfig<ClientsState> = {
    key: name,
    storage: storage,
    stateReconciler: autoMergeLevel1,
    whitelist: [fieldName<ClientsState>('displayPreferences'), fieldName<ClientsState>('tableFilters')],
};
const { reducer, actions } = clientsSlice;
const { modifyClientsDisplayPreferences, modifyClientsTableFilters } = actions;
export { reducer as clientsReducer };
export {
    fetchClientsThunk,
    deleteClientThunk,
    addClientThunk,
    updateClientThunk,
    modifyClientsDisplayPreferences,
    modifyClientsTableFilters,
};
